import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from '@/components/Iconify';
import { SectionTitle } from '@/features/users/shared/components';
import {
  getClientLinkedAccounts,
  getClients,
  linkClientAccounts,
  unlinkClientAccount,
} from '@/features/users/shared/api';
import type {
  ClientLinkedAccountsResult,
  LinkedClientAccount,
  LinkClientAccountsResult,
  UnlinkClientAccountResult,
  User,
} from '@/features/users/shared/types';
import { useGetAccess } from '@/hooks/useAccess';
import useAuth from '@/hooks/useAuth';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';

type LinkedAccountsSectionProps = {
  userId: string;
};

const EMPTY_LINKED_ACCOUNTS: LinkedClientAccount[] = [];

const getAccountName = (
  account?: Pick<
    LinkedClientAccount,
    'first_name' | 'last_name' | 'email' | 'id'
  > | null
) => {
  if (!account) return 'Unknown client';

  const name = [account.first_name, account.last_name]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean)
    .join(' ');

  return name || account.email || `Client #${account.id}`;
};

const getAccountInitials = (
  account?: Pick<
    LinkedClientAccount,
    'first_name' | 'last_name' | 'email' | 'id'
  > | null
) => {
  const name = getAccountName(account);
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
};

const getErrorMessage = (error: unknown) => {
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>;
    const message = record.message ?? record.error;

    if (typeof message === 'string') return message;
    if (Array.isArray(message)) return message.join(', ');
  }

  return 'Unable to update linked accounts';
};

export default function LinkedAccountsSection({
  userId,
}: LinkedAccountsSectionProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { canUpdate } = useGetAccess('clients');
  const { errorMsg, successMsg } = useSnackbarMsg();
  const [selectedClients, setSelectedClients] = useState<User[]>([]);
  const [validationError, setValidationError] = useState('');
  const [apiError, setApiError] = useState('');
  const clientId = Number(userId);
  const authUserType =
    user && typeof user === 'object' && 'user_type' in user
      ? (user as Record<string, unknown>).user_type
      : undefined;
  const isDashboardUser = authUserType == null || Number(authUserType) === 3;
  const canManageLinkedAccounts =
    canUpdate && isDashboardUser && Number.isFinite(clientId);

  const { data: linkedAccountsData, isLoading: isLinkedAccountsLoading } =
    useQuery({
      queryKey: ['clients', userId, 'linked-accounts'],
      queryFn: () => getClientLinkedAccounts(clientId),
      enabled: canManageLinkedAccounts,
      onError: (error) => {
        errorMsg(error, 'Unable to load linked accounts');
      },
      refetchOnWindowFocus: false,
    });

  const { data: clients = [], isLoading: isClientsLoading } = useQuery({
    queryKey: ['clients', 'account-linking-options'],
    queryFn: () => getClients(1),
    enabled: canManageLinkedAccounts,
    onError: (error) => {
      errorMsg(error, 'Unable to load client accounts');
    },
    refetchOnWindowFocus: false,
  });

  const linkedAccounts = linkedAccountsData?.accounts ?? EMPTY_LINKED_ACCOUNTS;

  const linkedAccountIds = useMemo(
    () => new Set(linkedAccounts.map((account) => account.id)),
    [linkedAccounts]
  );
  const selectedClientIds = useMemo(
    () => new Set(selectedClients.map((selectedClient) => selectedClient.id)),
    [selectedClients]
  );

  const availableClients = useMemo(
    () =>
      clients.filter(
        (account) =>
          account.id !== clientId &&
          (!linkedAccountIds.has(account.id) ||
            selectedClientIds.has(account.id))
      ),
    [clientId, clients, linkedAccountIds, selectedClientIds]
  );

  const linkAccountsMutation = useMutation({
    mutationFn: (userIds: number[]) =>
      linkClientAccounts(clientId, { user_ids: userIds }),
    onSuccess: (result: LinkClientAccountsResult) => {
      queryClient.setQueryData<ClientLinkedAccountsResult>(
        ['clients', userId, 'linked-accounts'],
        { accounts: result.linked_accounts }
      );
      queryClient.invalidateQueries({ queryKey: ['clients'], exact: true });
      queryClient.invalidateQueries({
        queryKey: ['clients', 'account-linking-options'],
        exact: true,
      });
      queryClient.invalidateQueries({
        queryKey: ['clients', userId, 'linked-accounts'],
        exact: true,
        refetchType: 'none',
      });
      setSelectedClients([]);
      setValidationError('');
      setApiError('');
      successMsg('Linked accounts updated');
    },
    onError: (error) => {
      const message = getErrorMessage(error);
      setApiError(message);
      errorMsg(error, 'Unable to update linked accounts');
    },
  });

  const unlinkAccountMutation = useMutation({
    mutationFn: (linkedUserId: number) =>
      unlinkClientAccount(clientId, linkedUserId),
    onSuccess: (result: UnlinkClientAccountResult) => {
      queryClient.setQueryData<ClientLinkedAccountsResult>(
        ['clients', userId, 'linked-accounts'],
        { accounts: result.linked_accounts }
      );
      queryClient.invalidateQueries({ queryKey: ['clients'], exact: true });
      queryClient.invalidateQueries({
        queryKey: ['clients', 'account-linking-options'],
        exact: true,
      });
      queryClient.invalidateQueries({
        queryKey: ['clients', userId, 'linked-accounts'],
        exact: true,
        refetchType: 'none',
      });
      setApiError('');
      successMsg('Linked account removed');
    },
    onError: (error) => {
      const message = getErrorMessage(error);
      setApiError(message);
      errorMsg(error, 'Unable to remove linked account');
    },
  });

  const handleSave = () => {
    const selectedIds = selectedClients
      .map((selectedClient) => selectedClient.id)
      .filter((id) => id !== clientId);

    if (!selectedIds.length) {
      setValidationError('Select at least one client account to link.');
      return;
    }

    setValidationError('');
    setApiError('');
    linkAccountsMutation.mutate(selectedIds);
  };

  const handleRemoveLinkedAccount = (linkedUserId: number) => {
    setValidationError('');
    setApiError('');
    unlinkAccountMutation.mutate(linkedUserId);
  };

  if (!canManageLinkedAccounts) return null;

  return (
    <Card sx={{ p: 3 }}>
      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', md: 'flex-start' }}
        >
          <Box sx={{ flex: 1 }}>
            <SectionTitle title="Linked accounts" />
          </Box>
          <LoadingButton
            variant="contained"
            loading={linkAccountsMutation.isLoading}
            disabled={!selectedClients.length}
            onClick={handleSave}
          >
            Save linked accounts
          </LoadingButton>
        </Stack>

        {(validationError || apiError) && (
          <Alert severity="error">{validationError || apiError}</Alert>
        )}

        <Autocomplete
          multiple
          openOnFocus
          filterSelectedOptions
          loading={isClientsLoading}
          options={availableClients}
          value={selectedClients}
          onChange={(_event, value) => {
            setSelectedClients(
              value.filter((account) => account.id !== clientId)
            );
            setValidationError('');
          }}
          getOptionLabel={(option) => getAccountName(option)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          noOptionsText="No available client accounts"
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar
                  src={option.profile_image}
                  alt={getAccountName(option)}
                  sx={{ width: 32, height: 32 }}
                >
                  {getAccountInitials(option)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {getAccountName(option)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ display: 'block' }}
                  >
                    {option.email}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.id}
                label={getAccountName(option)}
                avatar={
                  <Avatar
                    src={option.profile_image}
                    alt={getAccountName(option)}
                  >
                    {getAccountInitials(option)}
                  </Avatar>
                }
                sx={{ borderRadius: 0.75 }}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Add linked accounts"
              placeholder={selectedClients.length ? '' : 'Search clients'}
              helperText="Search by client name or email."
            />
          )}
        />

        {!linkedAccounts.length ? (
          <Typography variant="body2" color="text.secondary">
            {isLinkedAccountsLoading
              ? 'Loading linked accounts...'
              : 'No linked accounts'}
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {linkedAccounts.map((account) => (
              <Stack
                key={account.id}
                direction="row"
                spacing={1.5}
                alignItems="center"
              >
                <Avatar
                  src={account.profile_image ?? undefined}
                  alt={getAccountName(account)}
                >
                  {getAccountInitials(account)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap>
                    {getAccountName(account)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ display: 'block' }}
                  >
                    {account.email ?? `Client #${account.id}`}
                  </Typography>
                </Box>
                <Box sx={{ flexGrow: 1 }} />
                <Tooltip title="Remove linked account">
                  <span>
                    <IconButton
                      aria-label={`Remove linked account ${getAccountName(
                        account
                      )}`}
                      color="error"
                      size="small"
                      disabled={unlinkAccountMutation.isLoading}
                      onClick={() => handleRemoveLinkedAccount(account.id)}
                    >
                      {unlinkAccountMutation.isLoading &&
                      unlinkAccountMutation.variables === account.id ? (
                        <CircularProgress color="inherit" size={18} />
                      ) : (
                        <Iconify icon="eva:trash-2-outline" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
